import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";

import api from "../utilities/api.js";
import DeleteConfirmModal from "../components/admin/DeleteConfirmModal.jsx";
import DeleteAllRefsButton from "../components/admin/DeleteAllRefsButton.jsx";
import { FileInput } from "./ToolsPage-Import.jsx";
import { standardsImportFrameworkOptions } from "./ToolsPage-options.js";

const frameworkLabel = (value) =>
  standardsImportFrameworkOptions.find((o) => o.value === value)?.label ?? value;

// Helper component for a group of checkboxes (multiple selection);
// className sets its size in the surrounding flex container
const CheckboxGroup = ({
  title,
  options,
  selectedValues,
  onToggle,
  className = "flex-1 h-full",
}) => (
  <div
    className={`${className} bg-gray-100 p-4 rounded-md border border-gray-200 overflow-y-auto`}
  >
    <h3 className="text-lg font-medium mb-2 text-gray-800">{title}:</h3>
    <div className="flex flex-col space-y-2">
      {options.length === 0 && (
        <span className="text-gray-500 italic">None available</span>
      )}
      {options.map((option) => (
        <label key={option.value} className="inline-flex items-center">
          <input
            type="checkbox"
            value={option.value}
            checked={selectedValues.includes(option.value)}
            onChange={() => onToggle(option.value)}
            className="form-checkbox h-4 w-4 text-cyan-700 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="ml-2 text-gray-700">{option.label}</span>
        </label>
      ))}
    </div>
  </div>
);

CheckboxGroup.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  className: PropTypes.string,
};

// Collapsible list of OFIDs or skipped rows
const DetailsList = ({ title, items }) =>
  items.length > 0 && (
    <details className="mt-1">
      <summary className="cursor-pointer">
        {title} ({items.length})
      </summary>
      <div className="mt-1 max-h-32 overflow-y-auto text-xs break-words">
        {items.join(", ")}
      </div>
    </details>
  );

DetailsList.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

// What the import will delete and add, as returned by /import/standards-preview
const PreviewPanel = ({ preview }) => {
  const { summary, totals, skipped, drafts } = preview;
  return (
    <div className="mb-6 bg-gray-100 text-gray-800 p-4 rounded-md border border-gray-200 text-sm">
      <h3 className="text-lg font-medium mb-2">Preview</h3>
      <ul className="space-y-2">
        {summary.map((entry) => (
          <li key={`${entry.standard}|${entry.framework}`}>
            <span className="font-semibold">
              {entry.standard} / {frameworkLabel(entry.framework)}
            </span>
            : delete {entry.refsDeleted}, add {entry.refsAdded} references (
            {entry.recordsAffected} records)
            <DetailsList
              title="Records with existing data"
              items={entry.recordsWithExistingData}
            />
          </li>
        ))}
      </ul>
      <p className="mt-3 font-semibold">
        {totals.recordsChanged} records will change.
      </p>

      <div className="mt-2">
        <DetailsList
          title="Skipped: reference to itself"
          items={skipped.selfRefs.map((s) => `${s.ofid} (${s.standard})`)}
        />
        <DetailsList
          title="Skipped: OFID not found"
          items={skipped.unknownOfids.map(
            (s) => `${s.ofid} (${s.standard} / ${frameworkLabel(s.framework)}${s.from ? `, from ${s.from}` : ""})`,
          )}
        />
        <DetailsList
          title="Skipped: rows without a standard row"
          items={skipped.orphans.map((s) => `line ${s.line}: ${s.ofid} ${s.standard} ${s.entry}`)}
        />
        <DetailsList
          title="Kept: references without context on records shared by several standards"
          items={skipped.legacyKept.map((s) => `${s.ofid} (${frameworkLabel(s.framework)})`)}
        />
      </div>

      {drafts.length > 0 && (
        <div className="mt-3 p-2 bg-yellow-100 border border-yellow-300 rounded">
          Open drafts exist for {drafts.length} affected records. Approving
          them would overwrite the imported references: {drafts.join(", ")}
        </div>
      )}
    </div>
  );
};

PreviewPanel.propTypes = {
  preview: PropTypes.shape({
    summary: PropTypes.array.isRequired,
    totals: PropTypes.object.isRequired,
    skipped: PropTypes.object.isRequired,
    drafts: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
};

const buttonClass =
  "flex-1 bg-cyan-600 text-white hover:bg-gray-200 hover:text-gray-800 disabled:opacity-50 font-semibold py-3 px-4 border border-gray-300 rounded-md shadow-md transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-opacity-75";

const errorMessage = (error) =>
  error.response?.data?.message || error.response?.data?.error || error.message || "Unknown error";

// Import Standards Section Component (Admin only)
// Deletes and re-imports the standardRefs of the selected standards/frameworks
// from a mapping CSV (ofid, standard, entry, tag)
const StandardsImportSection = ({ dynamicHeight }) => {
  const [standardOptions, setStandardOptions] = useState([]);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/standards`)
      .then((response) =>
        setStandardOptions(
          response.data
            // FundsXML and openfunds are frameworks, not standards to import
            .filter((s) => !/^fundsxml\b/i.test(s.name) && s.name.toLowerCase() !== "openfunds")
            .map((s) => ({ label: s.name, value: s.name })),
        ),
      )
      .catch((error) => {
        console.error("Error fetching standards:", error);
        toast.error("Failed to load standards");
      });
  }, []);

  // Any change invalidates the preview
  const toggle = (setter) => (value) => {
    setter((values) =>
      values.includes(value) ? values.filter((v) => v !== value) : [...values, value],
    );
    setPreview(null);
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0] ?? null);
    setPreview(null);
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("standards", JSON.stringify(selectedStandards));
    formData.append("frameworks", JSON.stringify(selectedFrameworks));
    formData.append("file", file);
    return formData;
  };

  const handlePreview = async () => {
    if (!selectedStandards.length || !selectedFrameworks.length || !file) {
      toast.error("Please select standards, frameworks and a CSV file.", {
        duration: 5000,
        position: "top-center",
      });
      return;
    }
    if (file.name.split(".").pop().toLowerCase() !== "csv") {
      toast.error("Please choose a .csv file.", { duration: 5000, position: "top-center" });
      return;
    }
    setBusy(true);
    try {
      const response = await api.post(`/import/standards-preview`, buildFormData());
      setPreview(response.data);
    } catch (error) {
      console.error("Error previewing standards import:", error);
      toast.error("Preview failed!\n" + errorMessage(error), {
        duration: 6000,
        position: "top-center",
        className: "whitespace-pre-line",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const response = await api.post(`/import/standards-apply`, buildFormData());
      setPreview(null);
      toast.success("Import successful!\n" + response.data.message, {
        duration: 5000,
        position: "top-center",
        className: "whitespace-pre-line",
      });
    } catch (error) {
      console.error("Error applying standards import:", error);
      toast.error("Import failed!\n" + errorMessage(error), {
        duration: 6000,
        position: "top-center",
        className: "whitespace-pre-line",
      });
    } finally {
      setBusy(false);
    }
  };

  const combinations = preview
    ? preview.summary.map((e) => `${e.standard} / ${frameworkLabel(e.framework)}`).join(", ")
    : "";

  return (
    <div className="flex flex-col p-5 h-full lg:min-h-[30rem] border border-gray-100 rounded-lg w-full max-w-xl bg-gradient-to-r from-cyan-700 to-cyan-800 text-white">
      <h2 className="text-2xl font-bold text-gray-100 mb-4">Import Standards</h2>

      {/* CheckboxGroups Container */}
      <div className="flex gap-4 mb-6" style={{ height: `${dynamicHeight}px` }}>
        <CheckboxGroup
          title="Standards"
          options={standardOptions}
          selectedValues={selectedStandards}
          onToggle={toggle(setSelectedStandards)}
        />
        <CheckboxGroup
          title="Frameworks"
          options={standardsImportFrameworkOptions}
          selectedValues={selectedFrameworks}
          onToggle={toggle(setSelectedFrameworks)}
        />
      </div>

      {/* File Input Section */}
      <div className="mb-6 text-gray-100">
        <label className="block text-gray-100 text-lg font-bold mb-2">
          Choose Mapping CSV:
        </label>
        <FileInput
          onFileChange={handleFileChange}
          fileName={file?.name}
          className="w-full"
        />
      </div>

      {preview && <PreviewPanel preview={preview} />}

      <div className="flex gap-4 mt-auto">
        {preview ? (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            className={buttonClass}
          >
            Delete &amp; Import
          </button>
        ) : (
          <button
            type="button"
            onClick={handlePreview}
            disabled={busy}
            className={buttonClass}
          >
            Preview
          </button>
        )}
        <DeleteAllRefsButton
          className={buttonClass}
          disabled={busy}
          onDeleted={() => setPreview(null)}
        />
      </div>

      <DeleteConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleApply}
        title="Delete and re-import references"
        message={
          preview
            ? `This deletes ${preview.totals.refsDeleted} references for ${combinations} from the records and imports ${preview.totals.refsAdded} references from the file (${preview.totals.recordsChanged} records change). This cannot be undone.`
            : ""
        }
        confirmLabel="Delete & Import"
      />
    </div>
  );
};

StandardsImportSection.propTypes = {
  dynamicHeight: PropTypes.number.isRequired,
};

export { StandardsImportSection, CheckboxGroup };
